import { useState, useCallback, useRef } from 'react'
import { message } from 'antd'
import { config } from '../../../constants'
import {
  getSceneTimeline as apiGetSceneTimeline,
  regenerateSegment as apiRegenerateSegment,
  updatePause as apiUpdatePause,
  updateSegmentText as apiUpdateSegmentText,
  getTaskStatus as apiGetTaskStatus
} from '../../../services/api/aiVideoGeneration'

/**
 * useAudioEditor
 *
 * Centralised state + API layer for all audio editing operations.
 * Implements the full spec from the Audio Editing Endpoints doc.
 *
 * ─── Operations covered ──────────────────────────────────────────
 *  regenerateSegment   – re-TTS a voice segment (same or new text/speaker)
 *  updatePause         – resize a pause segment
 *  updateSegmentText   – edit text (preview: instant / commit: TTS rebuild)
 *  getTimeline         – read-only timeline refresh
 *
 * ─── State shape ─────────────────────────────────────────────────
 *  sceneStructure      – authoritative copy; replace entirely on each response
 *  selectedSegmentIdx  – 0-based index into segment_timings, null = none
 *  playheadMs          – current playback position in ms
 *  isPlaying           – global playback state
 *  pendingEdits        – { [segmentIndex]: { newText, estimatedDuration } }
 *  loadingSegment      – segment index currently being processed, null = idle
 *  isLoading           – any operation in flight
 *
 * ─── Usage ───────────────────────────────────────────────────────
 *  const editor = useAudioEditor({
 *    videoId,
 *    initialSceneStructure,
 *    sceneKey,
 *    narratorVoice,
 *    characterVoices,
 *    onSceneStructureChange,   // (updatedStructure) => void — persist upstream
 *  })
 */

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))

const pollTaskStatus = async (
  taskId,
  intervalMs = 2000,
  timeoutMs = 120000
) => {
  const start = Date.now()

  while (Date.now() - start < timeoutMs) {
    await sleep(intervalMs)

    const res = await apiGetTaskStatus(taskId)
    const data = res.data

    if (data.status === 'completed' || data.status === 'success') {
      return data.result !== undefined && data.result !== null
        ? data.result
        : data
    }
    if (data.status === 'failed' || data.status === 'error') {
      throw new Error(data.error || data.message || 'Task failed')
    }
    // still processing — keep polling
  }

  throw new Error('Audio editing timed out. Please try again.')
}

const handleApiTask = async apiPromise => {
  const res = await apiPromise
  const data = res.data

  if (data && data.task_id) {
    return pollTaskStatus(data.task_id)
  }

  return data
}

// ─────────────────────────────────────────────────────────────────

const useAudioEditor = ({
  videoId,
  initialSceneStructure = null,
  sceneKey,
  narratorVoice = null,
  characterVoices = {},
  token = null,
  onSceneStructureChange = null
}) => {
  // ── core state ──────────────────────────────────────────────────
  const [sceneStructure, setSceneStructure] = useState(initialSceneStructure)
  const [selectedSegmentIdx, setSelectedSegmentIdx] = useState(null)
  const [playheadMs, setPlayheadMs] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [pendingEdits, setPendingEdits] = useState({}) // { [idx]: { newText, estimatedDuration } }
  const [loadingSegment, setLoadingSegment] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  // Debounce ref for preview text edits
  const previewDebounceRef = useRef(null)

  // ── internal helpers ─────────────────────────────────────────────
  const applyUpdate = useCallback(
    updatedStructure => {
      setSceneStructure(updatedStructure)
      onSceneStructureChange && onSceneStructureChange(updatedStructure)
    },
    [onSceneStructureChange]
  )

  const currentScene =
    sceneStructure && sceneStructure[sceneKey] !== undefined
      ? sceneStructure[sceneKey]
      : null
  const segments =
    currentScene && currentScene.segment_timings !== undefined
      ? currentScene.segment_timings
      : []

  // ── 1. Regenerate Segment ────────────────────────────────────────
  /**
   * Re-TTS a single voice segment. Can optionally replace text or speaker.
   * Invalidates TTS cache for that segment only; all others served from cache.
   * ~10-20s
   */
  const regenerateSegment = useCallback(
    async ({ segmentIndex, newText = null, newSpeaker = null } = {}) => {
      const seg = segments[segmentIndex]
      if (!seg) {
        message.error('Segment not found')
        return
      }
      if (seg.type === 'pause') {
        message.error('Cannot regenerate a pause segment')
        return
      }

      setLoadingSegment(segmentIndex)
      setIsLoading(true)
      setError(null)

      try {
        const updated = await handleApiTask(
          apiRegenerateSegment({
            scene_structure: sceneStructure,
            scene_key: sceneKey,
            segment_index: segmentIndex,
            new_text: newText,
            new_speaker: newSpeaker,
            video_id: videoId
          })
        )

        applyUpdate(updated)

        // Clear any pending edit for this segment since audio is now committed
        setPendingEdits(prev => {
          const next = { ...prev }
          delete next[segmentIndex]
          return next
        })

        message.success('Segment regenerated')
      } catch (err) {
        setError({ segmentIndex, message: err.message })
        message.error(err.message || 'Regeneration failed')
      } finally {
        setLoadingSegment(null)
        setIsLoading(false)
      }
    },
    [
      sceneStructure,
      sceneKey,
      segments,
      narratorVoice,
      characterVoices,
      token,
      applyUpdate,
      videoId
    ]
  )

  // ── 2. Update Pause Duration ─────────────────────────────────────
  /**
   * Resize a pause segment. Zero TTS calls — just track rebuild.
   * ~5-15s
   */
  const updatePause = useCallback(
    async ({ segmentIndex, newDurationMs }) => {
      const seg = segments[segmentIndex]
      if (!seg) {
        message.error('Segment not found')
        return
      }
      if (seg.type !== 'pause') {
        message.error('Segment is not a pause')
        return
      }

      // Clamp to valid range
      const clamped = Math.max(100, Math.min(2000, newDurationMs))

      setLoadingSegment(segmentIndex)
      setIsLoading(true)
      setError(null)

      try {
        const updated = await handleApiTask(
          apiUpdatePause({
            scene_structure: sceneStructure,
            scene_key: sceneKey,
            segment_index: segmentIndex,
            new_duration_ms: clamped,
            narrator_voice: narratorVoice,
            character_voices: characterVoices,
            video_id: videoId
          })
        )

        applyUpdate(updated)
        message.success('Pause updated')
      } catch (err) {
        setError({ segmentIndex, message: err.message })
        message.error(err.message || 'Pause update failed')
      } finally {
        setLoadingSegment(null)
        setIsLoading(false)
      }
    },
    [
      sceneStructure,
      sceneKey,
      segments,
      narratorVoice,
      characterVoices,
      videoId,
      applyUpdate
    ]
  )

  // ── 3a. Preview Text Edit (instant, no TTS) ──────────────────────
  /**
   * Called on each keystroke (debounced).
   * Re-estimates timing immediately without TTS.
   * Sets audio_duration_estimated: true on the scene.
   * Returns instantly.
   */
  const previewTextEdit = useCallback(
    (segmentIndex, newText) => {
      // 1. Update local pending state immediately for UI responsiveness
      setPendingEdits(prev => ({
        ...prev,
        [segmentIndex]: { newText, committed: false }
      }))

      // 2. Debounced API call for timing re-estimation
      if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current)

      previewDebounceRef.current = setTimeout(async () => {
        try {
          const updated = await handleApiTask(
            apiUpdateSegmentText({
              scene_structure: sceneStructure,
              scene_key: sceneKey,
              segment_index: segmentIndex,
              new_text: newText,
              regenerate_audio: false, // PREVIEW MODE: Just re-estimate timing
              video_id: videoId
            })
          )

          // 3. Apply the updated structure (includes new segment_timings/durations)
          // This will cause the timeline bars to resize instantly in the UI.
          applyUpdate(updated)
        } catch (err) {
          console.error('Preview timing re-estimation failed:', err)
          // We don't show a message.error for every keystroke failure,
          // just let the pending ⚠ handle the visual feedback.
        }
      }, 500)
    },
    [sceneStructure, sceneKey, videoId, applyUpdate]
  )

  // ── 3b. Commit Text Edit (TTS + rebuild) ─────────────────────────
  /**
   * Called when user clicks "Save & Regenerate Audio".
   * Generates new TTS for the edited segment, rebuilds tracks.
   * ~10-20s
   */
  const commitTextEdit = useCallback(
    async (segmentIndex, newText) => {
      const seg = segments[segmentIndex]
      if (!seg) {
        message.error('Segment not found')
        return
      }
      if (seg.type === 'pause') {
        message.error('Cannot edit a pause segment')
        return
      }

      if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current)

      setLoadingSegment(segmentIndex)
      setIsLoading(true)
      setError(null)

      try {
        const updated = await handleApiTask(
          apiUpdateSegmentText({
            scene_structure: sceneStructure,
            scene_key: sceneKey,
            segment_index: segmentIndex,
            new_text: newText,
            regenerate_audio: true,
            video_id: videoId
          })
        )

        applyUpdate(updated)

        // Clear pending edit — audio is now committed
        setPendingEdits(prev => {
          const next = { ...prev }
          delete next[segmentIndex]
          return next
        })

        message.success('Audio updated')
      } catch (err) {
        setError({ segmentIndex, message: err.message })
        message.error(err.message || 'Audio update failed')
      } finally {
        setLoadingSegment(null)
        setIsLoading(false)
      }
    },
    [sceneStructure, sceneKey, segments, videoId, token, applyUpdate]
  )

  // ── 4. Get Timeline (read-only) ───────────────────────────────────
  /**
   * Refresh timeline data from the API.
   * Use after generation or when switching scenes.
   */
  const refreshTimeline = useCallback(async () => {
    if (!sceneStructure) return
    setIsLoading(true)
    setError(null)

    try {
      const updated = await handleApiTask(
        apiGetSceneTimeline({
          scene_structure: sceneStructure,
          scene_key: sceneKey,
          video_id: videoId
        })
      )
      applyUpdate(updated)
    } catch (err) {
      setError({ message: err.message })
      message.error(err.message || 'Timeline refresh failed')
    } finally {
      setIsLoading(false)
    }
  }, [sceneStructure, sceneKey, videoId, token, applyUpdate])

  // ── Segment selection ────────────────────────────────────────────
  const selectSegment = useCallback(idx => {
    setSelectedSegmentIdx(prev => (prev === idx ? null : idx))
  }, [])

  const clearSelection = useCallback(() => {
    setSelectedSegmentIdx(null)
  }, [])

  // ── Sync external scene structure (e.g. after initial generation) ─
  const syncSceneStructure = useCallback(newStructure => {
    setSceneStructure(newStructure)
    setPendingEdits({})
    setSelectedSegmentIdx(null)
    setError(null)
  }, [])

  // ── Derived helpers ───────────────────────────────────────────────
  const selectedSegment =
    selectedSegmentIdx != null ? segments[selectedSegmentIdx] : null

  const isSegmentLoading = idx => loadingSegment === idx

  const hasPendingEdit = idx => Boolean(pendingEdits[idx])

  const getPendingText = idx => {
    const pendingKey = pendingEdits[idx]
    if (pendingKey && pendingKey.newText !== undefined)
      return pendingKey.newText
    const segmentKey = segments[idx]
    if (segmentKey && segmentKey.text !== undefined) return segmentKey.text
    return ''
  }

  const hasError = idx => error && error.segmentIndex === idx

  return {
    // ── state ──────────────────────────────────────────────────
    sceneStructure,
    currentScene,
    segments,
    selectedSegmentIdx,
    selectedSegment,
    playheadMs,
    isPlaying,
    pendingEdits,
    loadingSegment,
    isLoading,
    error,

    // ── setters ────────────────────────────────────────────────
    setPlayheadMs,
    setIsPlaying,
    selectSegment,
    clearSelection,
    syncSceneStructure,

    // ── operations ─────────────────────────────────────────────
    regenerateSegment,
    updatePause,
    previewTextEdit,
    commitTextEdit,
    refreshTimeline,

    // ── helpers ────────────────────────────────────────────────
    isSegmentLoading,
    hasPendingEdit,
    getPendingText,
    hasError
  }
}

export default useAudioEditor
